/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog from '../../../ComponentLogger';
import OrientationComponentBase from './OrientationComponentBase';

/** @hidden */
// tslint:disable-next-line:typedef
const ccclass = cc._decorator.ccclass;
/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@ccclass("WidgetData")
class WidgetData
{
    @property()
    public topAnchor: boolean = false;
    @property ({ type: cc.Float, visible: (function(): boolean { return this.topAnchor; }) })
    public topOffset: number = 0.0;

    @property()
    public leftAnchor: boolean = false;
    @property({ type: cc.Float, visible: (function(): boolean { return this.leftAnchor; }) })
    public leftOffset: number = 0.0;

    @property()
    public rightAnchor: boolean = false;
    @property({ type: cc.Float, visible: (function(): boolean { return this.rightAnchor; }) })
    public rightOffset: number = 0.0;

    @property()
    public bottomAnchor: boolean = false;
    @property({ type: cc.Float, visible: (function(): boolean { return this.bottomAnchor; }) })
    public bottomOffset: number = 0.0;

    @property()
    public horizontalCenter: boolean = false;
    @property({ type: cc.Float, visible: (function(): boolean { return this.horizontalCenter; }) })
    public horizontalOffset: number = 0.0;

    @property()
    public verticalCenter: boolean = false;
    @property({ type: cc.Float, visible: (function(): boolean { return this.verticalCenter; }) })
    public verticalOffset: number = 0.0;

    @property (cc.Node)
    public target: cc.Node = null;
}

@ccclass
@cc._decorator.executeInEditMode
@cc._decorator.requireComponent(cc.Widget)
export default class OrientationWidget extends OrientationComponentBase
{
    @property({ type: WidgetData, visible: true })
    private _landscapeWidget: WidgetData = new WidgetData();
    @property({ type: WidgetData, visible: true })
    private _portraitWidget: WidgetData = new WidgetData();

    @property
    protected set setDataFromWidget(value: boolean) { this.setDataFromCurrentWidget(); }
    protected get setDataFromWidget(): boolean { return false; }

    private _widget: cc.Widget = null;

    protected onLoad(): void
    {
        super.onLoad();

        this._widget = this.getComponent(cc.Widget);
    }

    public static UpdateDataFromWidget(data: WidgetData, widget: cc.Widget): void
    {
        data.target = widget.target;

        data.topAnchor = widget.isAlignTop;
        data.leftAnchor = widget.isAlignLeft;
        data.rightAnchor = widget.isAlignRight;
        data.bottomAnchor = widget.isAlignBottom;

        data.horizontalCenter = widget.isAlignHorizontalCenter;
        data.verticalCenter = widget.isAlignVerticalCenter;

        data.topOffset = widget.top;
        data.leftOffset = widget.left;
        data.rightOffset = widget.right;
        data.bottomOffset = widget.bottom;

        data.horizontalOffset = widget.horizontalCenter;
        data.verticalOffset = widget.verticalCenter;
    }

    public SwitchOrientation(isLandscape: boolean): void
    {
        if (isLandscape)
        {
            this.updateWidget(this._landscapeWidget);
        }
        else
        {
            this.updateWidget(this._portraitWidget);
        }
    }

    protected updateWidget(data: WidgetData): void
    {
        if (!this._widget)
        {
            CLog.error("Missing Widget on Node: " + this.node.name);
            return;
        }

        this._widget.target = data.target;

        this._widget.isAlignTop = data.topAnchor;
        this._widget.isAlignLeft = data.leftAnchor;
        this._widget.isAlignRight = data.rightAnchor;
        this._widget.isAlignBottom = data.bottomAnchor;
        this._widget.isAlignHorizontalCenter = data.horizontalCenter;
        this._widget.isAlignVerticalCenter = data.verticalCenter;

        this._widget.top = data.topOffset;
        this._widget.left = data.leftOffset;
        this._widget.right = data.rightOffset;
        this._widget.bottom = data.bottomOffset;
        this._widget.horizontalCenter = data.horizontalOffset;
        this._widget.verticalCenter = data.verticalOffset;

        if (!CC_EDITOR)
        {
            this._widget.updateAlignment();
        }
    }

    private setDataFromCurrentWidget(): void
    {
        if (!CC_EDITOR || !this.canvas.HasValue()) { return; }

        const isLandscape: boolean = this.canvas.Value.IsDesignTimeLandscape();
        if (isLandscape === null) { return; }

        const currentWidget: WidgetData = isLandscape
            ? this._landscapeWidget
            : this._portraitWidget;
        OrientationWidget.UpdateDataFromWidget(currentWidget, this._widget);
    }
}
